import { useState, useEffect } from 'react';
import { TapGame } from '../components/TapGame';
import { Footer } from '../components/Footer';
import { GAME_GIRLS, UNLOCKED_KEY } from '../data/gameGirls';
import './Games.css';

function readUnlocked() {
    const raw = Number(localStorage.getItem(UNLOCKED_KEY));
    if (!raw || raw < 1) return 1;
    return Math.min(raw, GAME_GIRLS.length);
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

export function Games({ onNavigate, user, subStatus, setShowLogin, setShowPricing }) {
    const [unlocked, setUnlocked] = useState(readUnlocked);
    const [activeIdx, setActiveIdx] = useState(null);
    const [cleared, setCleared] = useState(false);

    const bumpUnlocked = (n) => {
        const capped = Math.min(n, GAME_GIRLS.length);
        setUnlocked(capped);
        try { localStorage.setItem(UNLOCKED_KEY, String(capped)); } catch {}
    };

    const enterLevel = (idx) => {
        if (idx + 1 > unlocked) return;
        if (!user) { setShowLogin(true); return; }
        if (subStatus !== 'active') { setShowPricing(true); return; }
        setActiveIdx(idx);
        setCleared(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const exitLevel = () => {
        setActiveIdx(null);
        setCleared(false);
    };

    const handleLevelCleared = () => {
        setCleared(true);
        if (activeIdx !== null && activeIdx + 1 >= unlocked) {
            bumpUnlocked(activeIdx + 2);
        }
    };

    const goToNext = () => {
        if (activeIdx === null) return;
        if (!user) { setShowLogin(true); return; }
        if (subStatus !== 'active') { setShowPricing(true); return; }
        const next = activeIdx + 1;
        if (next >= GAME_GIRLS.length) {
            exitLevel();
            return;
        }
        setActiveIdx(next);
        setCleared(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        // Preload roster covers so the grid never flickers when navigating back
        GAME_GIRLS.forEach(g => { const im = new Image(); im.src = g.cover; });
    }, []);

    const activeGirl = activeIdx !== null ? GAME_GIRLS[activeIdx] : null;
    const isFinal = activeIdx !== null && activeIdx + 1 >= GAME_GIRLS.length;

    return (
        <div className="gamesPage">
            <div className="gamesSection">
                {activeIdx === null ? (
                    <>
                        <section className="gamesHero">
                            <span className="gamesKicker">Games</span>
                            <h1 className="gamesTitle">
                                Unlock her, <span className="gamesTitleAccent">one tap at a time</span>.
                            </h1>
                            <p className="gamesSub">
                                Tap to reveal. Clear the level. Meet the next girl.
                            </p>
                        </section>

                        <div className="gamesProgress">
                            <span className="gamesProgressLabel">
                                Unlocked {pad2(unlocked)} / {pad2(GAME_GIRLS.length)}
                            </span>
                            <div className="gamesProgressDots">
                                {GAME_GIRLS.map((_, i) => (
                                    <div
                                        key={i}
                                        className={`gamesProgressDot${i < unlocked ? ' gamesProgressDot--on' : ''}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="gamesGrid">
                            {GAME_GIRLS.map((g, i) => {
                                const isUnlocked = i < unlocked;
                                const prev = i > 0 ? GAME_GIRLS[i - 1].name : null;
                                return (
                                    <button
                                        key={g.id}
                                        type="button"
                                        className={`gamesCard${isUnlocked ? '' : ' gamesCard--locked'}`}
                                        onClick={() => isUnlocked && enterLevel(i)}
                                        disabled={!isUnlocked}
                                    >
                                        <div className="gamesCardMedia">
                                            <img
                                                src={g.cover}
                                                alt={g.name}
                                                draggable={false}
                                                loading="lazy"
                                            />
                                            {!isUnlocked && (
                                                <div className="gamesCardLock">
                                                    <span className="gamesCardLockIcon">🔒</span>
                                                    <span className="gamesCardLockText">
                                                        Clear {prev} to unlock
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="gamesCardMeta">
                                            <span className="gamesCardLevel">Level {pad2(i + 1)}</span>
                                            <span className="gamesCardName">{g.name}</span>
                                            <span className="gamesCardTeaser">{g.teaser}</span>
                                            {isUnlocked && (
                                                <span className="gamesCardCta">Play →</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <>
                        <button className="gamesBack" onClick={exitLevel}>
                            ← Back to roster
                        </button>

                        <section className="gamesLevelHero">
                            <span className="gamesKicker">
                                Level {pad2(activeIdx + 1)} / {pad2(GAME_GIRLS.length)}
                            </span>
                            <h1 className="gamesTitle gamesTitle--level">{activeGirl.name}</h1>
                            <p className="gamesSub">{activeGirl.teaser}</p>
                        </section>

                        <div className="gamesLevelStage">
                            <TapGame
                                key={activeGirl.id}
                                photos={activeGirl.photos}
                                durationMs={activeGirl.durationMs}
                                noScroll
                                onDone={handleLevelCleared}
                            />
                        </div>

                        {cleared && (
                            <div className="gamesClearedOverlay" role="dialog" aria-label="Level cleared">
                                <div className="gamesClearedCard">
                                    <span className="gamesKicker">Level cleared</span>
                                    <h2 className="gamesClearedTitle">
                                        {isFinal
                                            ? 'You unlocked everyone.'
                                            : `Meet ${GAME_GIRLS[activeIdx + 1].name} →`}
                                    </h2>
                                    <img
                                        className="gamesClearedImg"
                                        src={isFinal ? activeGirl.finale : GAME_GIRLS[activeIdx + 1].cover}
                                        alt=""
                                        draggable={false}
                                    />
                                    <div className="gamesClearedActions">
                                        {!isFinal && (
                                            <button className="gamesClearedPrimary" onClick={goToNext}>
                                                Next girl →
                                            </button>
                                        )}
                                        <button className="gamesClearedGhost" onClick={exitLevel}>
                                            Back to roster
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Footer onNavigate={onNavigate} />
        </div>
    );
}
